import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ccdb_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ccdb_token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Keep-alive heartbeat — pings /api/health every 4 minutes while the tab
  // is open so the backend pod never gets cold-parked by the platform. A
  // cold-start on the next user click was the cause of the "Server is
  // warming up" toast; keeping the pod warm eliminates the class of bug
  // entirely for any user who has the app open. Uses `fetch` (not axios)
  // to bypass the auth interceptor — the ping is deliberately anonymous.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ping = () => {
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
          method: "GET",
          cache: "no-store",
          keepalive: true,
        }).catch(() => {});
      } catch { /* silent */ }
    };
    // Fire once on mount so the very first user action lands on a warm pod
    ping();
    const id = setInterval(ping, 4 * 60 * 1000); // every 4 minutes
    // Also warm up whenever the tab regains focus (mobile PWAs put the tab
    // to sleep aggressively).
    const onVisible = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  async function login(identifier, password) {
    const r = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("ccdb_token", r.data.access_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return r.data;
  }

  function logout() {
    localStorage.removeItem("ccdb_token");
    setUser(null);
  }

  function can(...perms) {
    if (!user) return false;
    if (user.is_super) return true;
    const u = new Set(user.permissions || []);
    return perms.some((p) => u.has(p));
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
